import React, { useEffect, useRef, useState } from "react";
import { useLink, useRouterType, useRouterContext, useNavigation } from "@refinedev/core";
import { Typography, message } from "antd";
import {
  CheckOutlined,
  MailOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_URL } from "../../../constants";
import styles from "./register.module.css";

const { Title, Text } = Typography;

export const VerifyEmailPage: React.FC = () => {
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;
  const { push } = useNavigation();

  const params = new URLSearchParams(window.location.search);
  const presetEmail = params.get("email") || "";

  const [email, setEmail] = useState(presetEmail);
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setResendCooldown(30);
    const t = setInterval(() => {
      setResendCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const handleCodeChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = digit;
    setCode(next);
    if (digit && idx < 5) codeRefs.current[idx + 1]?.focus();
  };

  const handleCodeKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) codeRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) codeRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) codeRefs.current[idx + 1]?.focus();
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      const arr = pasted.split("");
      const next = ["", "", "", "", "", ""];
      arr.forEach((d, i) => (next[i] = d));
      setCode(next);
      e.preventDefault();
      const nextEmpty = Math.min(arr.length, 5);
      codeRefs.current[nextEmpty]?.focus();
    }
  };

  const verifyCode = async () => {
    const c = code.join("");
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErrorMsg("Informe um e-mail válido.");
      return;
    }
    if (c.length !== 6) {
      setErrorMsg("Digite os 6 dígitos do código.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/auth/verify-email`, { email: email.trim().toLowerCase(), code: c });
      message.success("E-mail confirmado! Você já pode fazer login.");
      setTimeout(() => push("/login"), 1500);
    } catch (err: any) {
      const m = err?.response?.data?.message || "Código inválido ou expirado.";
      setErrorMsg(m);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0 || !email) return;
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email: email.trim().toLowerCase() });
      message.success("Se houver uma conta com este e-mail aguardando verificação, um novo código foi enviado.");
      setResendCooldown(60);
    } catch {
      message.error("Não foi possível reenviar agora.");
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* Painel esquerda */}
      <div className={styles.brandPanel}>
        <div className={styles.brandGlow} />
        <div className={styles.brandContent}>
          <img src="/images/logo.png" alt="Split Fácil" className={styles.brandLogo} />
          <h1 className={styles.brandHeadline}>Quase lá!</h1>
          <p className={styles.brandText}>
            Confirme seu e-mail com o código de 6 dígitos que enviamos para ativar sua conta no
            Split Fácil.
          </p>
        </div>
      </div>

      {/* Painel direita */}
      <div className={styles.formPanel}>
        <div className={styles.fadeUp}>
          <div className={styles.logoMobile}>
            <img src="/images/logo.png" alt="Split Fácil" />
          </div>

          <div className={styles.finalCard}>
            <div className={styles.finalIcon}>
              <MailOutlined />
            </div>
            <Title className={styles.finalTitle}>Verificar e-mail</Title>
            <Text className={styles.finalText}>
              Digite o código de 6 dígitos que enviamos para o seu e-mail.
            </Text>

            {!presetEmail && (
              <div style={{ margin: "0 0 1rem" }}>
                <input
                  type="email"
                  placeholder="Seu e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    height: "3rem",
                    padding: "0 1rem",
                    border: "2px solid #e2e8f0",
                    borderRadius: "0.65rem",
                    fontSize: "1rem",
                    color: "#0f172a",
                    outline: "none",
                  }}
                />
              </div>
            )}

            {errorMsg && <div className={styles.error}>{errorMsg}</div>}

            <div className={styles.codeBox} onPaste={handleCodePaste}>
              {code.map((c, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    codeRefs.current[i] = el;
                  }}
                  className={[styles.codeInput, c ? styles.filled : ""].join(" ")}
                  value={c}
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={1}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKey(i, e)}
                />
              ))}
            </div>

            <p className={styles.codeResend}>
              <button onClick={resendCode} disabled={resendCooldown > 0 || !email}>
                {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : "Reenviar código"}
              </button>
            </p>

            <button
              className={styles.primaryButton}
              onClick={verifyCode}
              disabled={loading || code.join("").length !== 6}
              style={{ width: "100%", marginTop: "1.25rem" }}
            >
              {loading ? <LoadingOutlined /> : <CheckOutlined />}
              {loading ? "Verificando..." : "Confirmar e ativar conta"}
            </button>

            <p className={styles.loginLink}>
              <ActiveLink to="/login">Voltar para o login</ActiveLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
